import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Row, Select, Spin, Tooltip, Modal } from "antd";
import debounce from "lodash/debounce";
import { checkIsLoading, getFio, getSelectOptions, selectFilterOption } from "../../../lib/utils";
import { IClientType } from "../../../lib/types";
import { useAppDispatch, useAppSelector } from "../../..";
import {
  fetchAccounts,
  agreementsSearchActions,
} from "../../../store/slices/agreementsSearch/agreementsSearch";
import { ReactComponent as IndividualIcon } from "../../../img/individual-icon.svg";
import { ReactComponent as OrganizationIcon } from "../../../img/organization-icon.svg";
import { ReactComponent as LoupeIcon } from "../../../img/loupe-icon.svg";
import { AgreementsSearchModal } from "../../AgreementsSearch";
import { hasAccess } from "../../RolesModule/hasAccess";
import {
  MENU_ACCOUNTS,
  MENU_ACCOUNTS_INDIVIDUAL,
  MENU_ACCOUNTS_INDIVIDUAL_WRITE,
  MENU_ACCOUNTS_ORGANIZATION,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
  MENU_ACCOUNTS_WRITE,
} from "../../../lib/const/permissions";
import "./AgreementsSearchFormItem.less";

interface ComponentProps {
  name: string;
  multiselect?: boolean;
  combineAgreements?: boolean;
  clientType: IClientType;
  setClientType: React.Dispatch<React.SetStateAction<IClientType>>;
  onChange?: (value?: string[]) => void;
}

export const AgreementsSearchFormItem = (props: ComponentProps) => {
  const {
    name,
    multiselect = false,
    combineAgreements = false,
    clientType,
    setClientType,
    onChange = () => {},
  } = props;
  const { accounts, accountsLoading } = useAppSelector((state) => state.agreementsSearch);
  const dispatch = useAppDispatch();

  const hasAccessToOrganization: boolean = useMemo(
    () =>
      hasAccess(
        MENU_ACCOUNTS_ORGANIZATION,
        MENU_ACCOUNTS_ORGANIZATION_WRITE,
        MENU_ACCOUNTS,
        MENU_ACCOUNTS_WRITE
      ),
    [hasAccess]
  );

  const hasAccessToIndividual: boolean = useMemo(
    () => hasAccess(MENU_ACCOUNTS_INDIVIDUAL, MENU_ACCOUNTS_INDIVIDUAL_WRITE),
    [hasAccess]
  );

  // Дефолтный тип клиента в поиске ЮЛ/ФЛ
  useEffect(() => {
    if (hasAccessToOrganization) {
      setClientType("ORGANIZATION");
    } else if (hasAccessToIndividual) {
      setClientType("INDIVIDUAL");
    }
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  // Кнопка изменения задизейблена, если доступен только один вид клиента
  const allowedClientChange = useMemo(() => {
    if (hasAccessToOrganization && hasAccessToIndividual) {
      return true;
    }
    return false;
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  // Стили кнопки изменения клиента
  const clientChangeButtonStyle = useMemo(
    () =>
      allowedClientChange
        ? {
            cursor: "pointer",
            color: "rgb(0, 187, 119)",
          }
        : {
            cursor: "not-allowed",
            color: "rgba(0, 187, 119, 0.25)",
          },
    [allowedClientChange]
  );

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const onCloseSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  const onSearch = debounce((name: string) => {
    if (name) {
      dispatch(fetchAccounts({ name, clientType }));
    }
  }, 800);

  const placeholder: string = useMemo(() => {
    switch (clientType) {
      case "ORGANIZATION":
        return "Введите наименование организации";
      case "INDIVIDUAL":
        return "Введите ФИО клиента";
    }
  }, [clientType]);

  const labelNode = (
    <div className="agreements-search-formItem__label">
      Клиент
      <Tooltip title="Открыть расширенный поиск клиента">
        <LoupeIcon
          onClick={() => {
            setIsSearchModalOpen(true);
          }}
        />
      </Tooltip>
    </div>
  );

  return (
    <div className="agreements-search-formItem">
      <Form.Item style={{ margin: 0 }} shouldUpdate label={labelNode}>
        {({ setFieldsValue, getFieldValue }) => {
          const toggleClientType = () => {
            if (allowedClientChange) {
              setClientType((prev) => (prev === "INDIVIDUAL" ? "ORGANIZATION" : "INDIVIDUAL"));
              dispatch(agreementsSearchActions.cleanAccounts());
              onChange();
              setFieldsValue({ [name]: undefined });
            }
          };

          const value = getFieldValue(name) || undefined;

          // При мультиселекте, может быть пустой массив, поэтому дополнительная проверка
          const valueIsEmpty = (() => {
            if (!value) return true;
            if (multiselect) return value.length === 0;
            return false;
          })();

          const onChangeClientType = (type: IClientType) => {
            if (clientType === type) return;
            if (valueIsEmpty) {
              toggleClientType();
            } else {
              Modal.confirm({
                content: "При переключении на новую вкладку список выбранных клиентов будет очищен",
                onOk: toggleClientType,
                cancelText: "Отмена",
                okText: "Выполнить переход",
              });
            }
          };

          const onChangeSelect = (value) => {
            onChange(value);
            setFieldsValue({ [name]: value });
          };

          const onSubmitSearchModal = (accountIds: number[]) => {
            setFieldsValue({ [name]: accountIds });
            onChange();
            onCloseSearchModal();
          };

          const selectOptions = getSelectOptions({
            list: accounts.map(({ id, organization, individual }) => {
              const name =
                clientType === "ORGANIZATION"
                  ? organization?.fullName
                  : getFio({
                      firstName: individual?.firstName,
                      lastName: individual?.lastName,
                      middleName: individual?.middleName,
                    }) || String(id);
              return { id, name };
            }),
          });

          return (
            <div>
              <Form.Item
                name={name}
                getValueFromEvent={
                  () =>
                    undefined /* некорректно отображение при мультеселекте, поэтому нужно обнулять */
                }
              >
                <Row>
                  <Col span={21}>
                    <Select
                      showSearch
                      allowClear
                      style={{ width: "100%" }}
                      notFoundContent={checkIsLoading(accountsLoading) && <Spin size="small" />}
                      onSearch={onSearch}
                      placeholder={placeholder}
                      onChange={onChangeSelect}
                      value={value}
                      mode={multiselect ? "multiple" : undefined}
                      options={selectOptions}
                      filterOption={selectFilterOption}
                    />
                  </Col>
                  <Col>
                    <div className="agreements-search-formItem__clientIcon">
                      {clientType === "ORGANIZATION" && (
                        <OrganizationIcon
                          onClick={() => onChangeClientType("INDIVIDUAL")}
                          style={clientChangeButtonStyle}
                        />
                      )}
                      {clientType === "INDIVIDUAL" && (
                        <IndividualIcon
                          onClick={() => onChangeClientType("ORGANIZATION")}
                          style={clientChangeButtonStyle}
                        />
                      )}
                    </div>
                  </Col>
                </Row>
              </Form.Item>
              <AgreementsSearchModal
                clientType={clientType}
                visible={isSearchModalOpen}
                onClose={onCloseSearchModal}
                onSubmit={onSubmitSearchModal}
                toggleClientType={toggleClientType}
                multiselect={multiselect}
                combineAgreements={combineAgreements}
              />
            </div>
          );
        }}
      </Form.Item>
    </div>
  );
};
